import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon, DownloadIcon, RefreshIcon } from './Icons';
import {
  ADDRESS_META, PAYMENT_META, PROGRESS_STEPS, STATUS_META, StatusPill, describeEvent, formatDate, formatDateTime,
  COURIER_MARKUP_PERCENT, customerShippingPaise, formatMoney, formatQty, nextStep, progressIndex, qtyUnit, readJson, staleUnpaidDays, userCaps, confirmOrderDelete,
} from './intlOrderUtils';
import { CustomerFields, ProductPicker, customerFromOrder, isCustomItem, itemsPayload, linesFromItems } from './IntlOrderForm';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const addressLines = (order) =>
  [
    order.address_line1,
    order.address_line2,
    [order.city, order.state, order.postal_code].filter(Boolean).join(', '),
    order.country,
  ].filter(Boolean);

const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// Opens the order as a printable page; the browser's "Save as PDF" makes the
// file (no PDF library needed, and the ERP product images load as plain <img>).
const downloadOrderPdf = (order) => {
  const win = window.open('', '_blank');
  if (!win) {
    window.alert('Allow pop-ups for this site to download the PDF.');
    return;
  }
  const money = (paise) => escapeHtml(formatMoney(paise, order.currency));
  const rows = order.items.map((item) => `
    <tr>
      <td><div class="product">${item.image_url ? `<img src="${escapeHtml(item.image_url)}" alt="">` : '<span class="noimg"></span>'}
        <div>${escapeHtml(item.name)}${item.sku ? `<div class="sku">SKU: ${escapeHtml(item.sku)}</div>` : ''}</div></div></td>
      <td class="num">${escapeHtml(`${formatQty(item.qty)} ${qtyUnit(item)}`)}</td>
      <td class="num">${money(item.line_total_paise)}</td>
    </tr>`).join('');
  // Products only: shipping and discount are left out of this PDF on purpose.
  const totals = `<tr><td colspan="2" class="num">Total</td><td class="num">${money(order.product_subtotal_paise)}</td></tr>`;
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(order.order_number)}</title><style>
    body { font-family: Arial, sans-serif; color: #111; margin: 32px; }
    h1 { font-size: 20px; margin: 0 0 4px; } h2 { font-size: 14px; margin: 24px 0 8px; text-transform: uppercase; color: #555; }
    .muted { color: #666; font-size: 12px; } .address div { line-height: 1.5; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border-bottom: 1px solid #ddd; padding: 8px; text-align: left; vertical-align: middle; }
    th { background: #f4f4f4; } .num { text-align: right; white-space: nowrap; }
    .product { display: flex; align-items: center; gap: 10px; }
    .product img, .noimg { width: 56px; height: 56px; object-fit: cover; border-radius: 4px; border: 1px solid #ddd; flex: none; }
    .sku { color: #666; font-size: 11px; } tfoot tr:last-child td { font-weight: bold; }
    tr { page-break-inside: avoid; }
  </style></head><body>
    <h1>Order ${escapeHtml(order.order_number)}</h1>
    <div class="muted">${escapeHtml(formatDate(order.created_at))}</div>
    <h2>Customer &amp; shipping address</h2>
    <div class="address">
      <div><strong>${escapeHtml(order.customer_name)}</strong></div>
      ${addressLines(order).map((line) => `<div>${escapeHtml(line)}</div>`).join('')}
      ${order.customer_number ? `<div>Phone: ${escapeHtml(order.customer_number)}</div>` : ''}
      ${order.email ? `<div>Email: ${escapeHtml(order.email)}</div>` : ''}
    </div>
    <h2>Products</h2>
    <table><thead><tr><th>Product</th><th class="num">Qty</th><th class="num">Total</th></tr></thead>
      <tbody>${rows}</tbody><tfoot>${totals}</tfoot></table>
  </body></html>`);
  win.document.close();
  // Print only once the product images have loaded (or failed), so they're in the PDF.
  const images = Array.from(win.document.images);
  Promise.all(images.map((img) => (img.complete ? null : new Promise((done) => { img.onload = done; img.onerror = done; }))))
    .then(() => { win.focus(); win.print(); });
};

const byLine = (who, when) => (who ? `${who} · ${formatDateTime(when)}` : null);

function Field({ label, children }) {
  return (
    <div className="intl-field">
      <dt>{label}</dt>
      <dd>{children || children === 0 ? children : '-'}</dd>
    </div>
  );
}

function Section({ title, action, children }) {
  return (
    <section className="intl-section">
      <div className="intl-section-head">
        <h3 className="intl-section-title">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

// Small dialog shell for the edit/confirm/cancel forms on this page.
function Dialog({ title, subtitle, wide, onClose, children }) {
  return (
    <div className="modal-overlay modal-overlay-center intl-dialog-overlay">
      <div className={`modal-panel ${wide ? 'intl-modal-wide' : 'modal-panel-compose'}`}>
        <div className="modal-header">
          <div>
            <h2>{title}</h2>
            {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function DialogActions({ onCancel, busy, submitLabel, danger, disabled }) {
  return (
    <div className="follow-up-add-actions">
      {onCancel && (
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      )}
      <button type="submit" className={`btn ${danger ? 'btn-danger-outline' : 'btn-primary'}`} disabled={busy || disabled}>
        {busy ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

// Order -> Logistics -> Address -> Payment -> Packing -> Dispatch -> Delivered
function ProgressBar({ status }) {
  const current = progressIndex(status);
  const cancelled = status === 'CANCELLED';
  return (
    <ol className={`intl-progress ${cancelled ? 'intl-progress-cancelled' : ''}`} aria-label="Order progress">
      {PROGRESS_STEPS.map((label, index) => {
        const state = cancelled ? 'todo' : index < current ? 'done' : index === current ? 'active' : 'todo';
        return (
          <li key={label} className={`intl-progress-step intl-progress-${state}`} aria-current={state === 'active' ? 'step' : undefined}>
            <span className="intl-progress-dot">{state === 'done' ? '✓' : index + 1}</span>
            <span className="intl-progress-label">{label}</span>
          </li>
        );
      })}
    </ol>
  );
}

function AddressBlock({ order, title = 'Ship to' }) {
  const lines = addressLines(order);
  return (
    <div className="intl-shipto">
      <div className="intl-shipto-title">{title}</div>
      <div className="intl-shipto-name">{order.customer_name}</div>
      {lines.length ? lines.map((line) => <div key={line}>{line}</div>) : <span className="intl-muted">No address yet</span>}
      <div className="intl-muted">WhatsApp {order.customer_number}{order.email ? ` · ${order.email}` : ''}</div>
    </div>
  );
}

// Courier charge (ADDRESS_VERIFIED), or just notes/weight/service later on
// (`quote` false). Logistics only saves: the order team generates the
// payment link (PaymentLinkPanel).
function LogisticsForm({ order, quote, onSave, onCancel }) {
  const [form, setForm] = useState({
    courier_service: order.courier_service || '',
    shipping_charge: order.shipping_charge_paise === null ? '' : String(order.shipping_charge_paise / 100),
    package_weight_kg: order.package_weight_grams ? String(order.package_weight_grams / 1000) : '',
    package_length_cm: order.package_length_cm ?? '',
    package_width_cm: order.package_width_cm ?? '',
    package_height_cm: order.package_height_cm ?? '',
    logistics_notes: order.logistics_notes || '',
  });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });

  const submit = async () => {
    setBusy('save');
    setError('');
    try {
      const body = { ...form };
      if (!quote) delete body.shipping_charge;
      await onSave(body);
      setBusy(null);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };

  const charge = form.shipping_charge === '' ? null : Math.round(Number(form.shipping_charge) * 100);
  const validCharge = charge !== null && !Number.isNaN(charge) && charge >= 0;
  return (
    <form className="intl-inline-form" onSubmit={(event) => { event.preventDefault(); submit(); }}>
      <div className="intl-form-grid">
        {quote && (
          <label className="login-field">
            <span>Courier charge (₹) *</span>
            <input type="number" min="0" step="0.01" value={form.shipping_charge} onChange={set('shipping_charge')} autoFocus />
          </label>
        )}
        <label className="login-field">
          <span>Courier / service</span>
          <input type="text" value={form.courier_service} onChange={set('courier_service')} placeholder="e.g. DHL Express" />
        </label>
        <label className="login-field">
          <span>Package weight (kg)</span>
          <input type="number" min="0" step="0.001" value={form.package_weight_kg} onChange={set('package_weight_kg')} />
        </label>
        <div className="login-field">
          <span>Dimensions (cm) L × W × H</span>
          <div className="intl-dimensions">
            <input type="number" min="0" step="0.1" value={form.package_length_cm} onChange={set('package_length_cm')}
              placeholder="L" aria-label="Package length (cm)" />
            <span aria-hidden="true">×</span>
            <input type="number" min="0" step="0.1" value={form.package_width_cm} onChange={set('package_width_cm')}
              placeholder="W" aria-label="Package width (cm)" />
            <span aria-hidden="true">×</span>
            <input type="number" min="0" step="0.1" value={form.package_height_cm} onChange={set('package_height_cm')}
              placeholder="H" aria-label="Package height (cm)" />
          </div>
        </div>
      </div>
      <label className="login-field">
        <span>Logistics notes</span>
        <textarea rows={2} value={form.logistics_notes} onChange={set('logistics_notes')} />
      </label>
      {quote && (
        <div className="intl-totals intl-totals-preview">
          <div><span>Product total</span><span>{formatMoney(order.product_subtotal_paise, order.currency)}</span></div>
          <div><span>Courier charge</span><span>{validCharge ? formatMoney(charge, order.currency) : '-'}</span></div>
          {order.discount_paise > 0 && (
            <div><span>Discount</span><span>- {formatMoney(order.discount_paise, order.currency)}</span></div>
          )}
          <div className="intl-totals-final">
            <span>Final total</span>
            <span>{validCharge ? formatMoney(order.product_subtotal_paise + charge - (order.discount_paise || 0), order.currency) : '-'}</span>
          </div>
        </div>
      )}
      {error && <div className="login-error">{error}</div>}
      <div className="follow-up-add-actions">
        {onCancel && <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={Boolean(busy)}>Cancel</button>}
        <button type="submit" className={`btn ${quote ? 'btn-primary' : 'btn-secondary'}`} disabled={Boolean(busy)}>
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
      </div>
      {quote && (
        <p className="intl-muted intl-form-hint">
          After you save, the order team checks the total, adds any extra item, and generates the payment link.
          {order.shipping_charge_paise === null && ' The first time the courier charge is saved, the order team gets the final amount on WhatsApp.'}
        </p>
      )}
    </form>
  );
}

// Every product has to be ticked before the order can be marked packed;
// the server checks the same list.
function PackingChecklist({ order, canPack, busy, onPacked }) {
  const [ticked, setTicked] = useState(() => new Set());
  const toggle = (id) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const allTicked = order.items.length > 0 && order.items.every((item) => ticked.has(item.id));
  return (
    <div>
      <ul className="intl-packlist">
        {order.items.map((item) => (
          <li key={item.id} className={ticked.has(item.id) ? 'intl-packlist-done' : ''}>
            <label>
              {canPack && <input type="checkbox" checked={ticked.has(item.id)} onChange={() => toggle(item.id)} />}
              {item.image_url ? <img src={item.image_url} alt="" className="intl-thumb" /> : <span className="intl-thumb" />}
              <span className="intl-packlist-name">
                {item.name}
                {item.sku && <span className="intl-muted"> · {item.sku}</span>}
              </span>
              <span className="intl-packlist-qty">
                Qty {formatQty(item.qty)} {qtyUnit(item)}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {canPack && (
        <div className="follow-up-add-actions">
          <span className="intl-muted">{ticked.size} of {order.items.length} packed</span>
          <button type="button" className="btn btn-primary" disabled={!allTicked || busy} onClick={() => onPacked([...ticked])}>
            {busy ? 'Saving…' : 'Mark as Packed'}
          </button>
        </div>
      )}
    </div>
  );
}

function DispatchForm({ order, onSave, onCancel }) {
  const s = order.shipment || {};
  const editing = order.status === 'SHIPPED';
  const [form, setForm] = useState({
    courier_name: s.courier_name || order.courier_service || '',
    awb_number: s.awb_number || '',
    tracking_url: s.tracking_url || '',
    shipment_date: s.shipment_date || todayStr(),
    notes: s.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (field) => (event) => setForm({ ...form, [field]: event.target.value });
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(form);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <form className="intl-inline-form" onSubmit={submit}>
      <div className="intl-form-grid">
        <label className="login-field">
          <span>Courier / shipping provider *</span>
          <input type="text" value={form.courier_name} onChange={set('courier_name')} required autoFocus />
        </label>
        <label className="login-field">
          <span>AWB / tracking number *</span>
          <input type="text" value={form.awb_number} onChange={set('awb_number')} required />
        </label>
        <label className="login-field intl-form-wide">
          <span>Tracking URL</span>
          <input type="url" value={form.tracking_url} onChange={set('tracking_url')} placeholder="https://…" />
        </label>
        <label className="login-field">
          <span>Dispatch date</span>
          <input type="date" value={form.shipment_date} onChange={set('shipment_date')} />
        </label>
      </div>
      <label className="login-field">
        <span>Shipping notes</span>
        <textarea rows={2} value={form.notes} onChange={set('notes')} />
      </label>
      {error && <div className="login-error">{error}</div>}
      <DialogActions onCancel={onCancel} busy={busy} submitLabel={editing ? 'Save courier & AWB' : 'Complete Dispatch'} />
    </form>
  );
}

// Full-page view of one international order: progress bar, what happens
// next, the panel for the current stage, then the full record. Every
// action calls a dedicated server route; the server decides whether it's
// allowed and returns the updated order.
function IntlOrderDetail({ orderId, onClose, onChanged, onDeleted }) {
  const { authFetch, user } = useAuth();
  const caps = userCaps(user);
  const [order, setOrder] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const [dialog, setDialog] = useState(null); // 'customer' | 'items' | 'confirm' | 'cancel' | 'reopen' | 'recheck'
  const [panel, setPanel] = useState(null); // 'dispatch' | 'tracking' | 'logistics'
  // Whether packing is done in the ERP (server: ERP_PACKING_API_KEY set).
  // Kept apart from `order`: only the order read carries it, action replies don't.
  const [erpPacking, setErpPacking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [orderBody, historyBody] = await Promise.all([
        readJson(await authFetch(`/api/international-orders/${orderId}`), 'Could not load this order.'),
        readJson(await authFetch(`/api/international-orders/${orderId}/history`), 'Could not load order history.'),
      ]);
      setOrder(orderBody);
      setErpPacking(Boolean(orderBody.erp_packing));
      setEvents(historyBody.events || []);
      setError('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, orderId]);

  useEffect(() => {
    load();
  }, [load]);

  // WhatsApp messages are sent in the background after the save; poll
  // briefly until the queued ones have a result.
  const messagesInFlight = (order?.notifications || []).some((n) => n.status === 'pending' || n.status === 'sending');
  useEffect(() => {
    if (!messagesInFlight) return undefined;
    const timer = setTimeout(load, 2500);
    return () => clearTimeout(timer);
  }, [messagesInFlight, order, load]);

  // Packing happens in the ERP, which sends no event here: re-read the order
  // while it waits, so an open page moves to Ready for Dispatch by itself.
  const awaitingErpPacking = ['PAYMENT_CONFIRMED', 'PACKING'].includes(order?.status);
  useEffect(() => {
    if (!awaitingErpPacking) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [awaitingErpPacking, load]);

  // Every action returns the updated order; history is re-read so the
  // activity list always matches what the server recorded. Forms and
  // dialogs pass inline: true and show the error themselves.
  const run = async (key, path, { method = 'POST', body, success, inline = false } = {}) => {
    setBusy(key);
    setActionError('');
    setNotice('');
    try {
      const updated = await readJson(
        await authFetch(`/api/international-orders/${orderId}${path}`, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: body ? JSON.stringify(body) : undefined,
        }),
        'Something went wrong. Please try again.'
      );
      setOrder(updated);
      const historyBody = await readJson(await authFetch(`/api/international-orders/${orderId}/history`), '').catch(() => null);
      if (historyBody) setEvents(historyBody.events || []);
      if (success) setNotice(typeof success === 'function' ? success(updated) : success);
      onChanged?.();
      return updated;
    } catch (err) {
      if (!inline) setActionError(err.message);
      // A failed save may still have saved part of the change (e.g. the
      // courier charge before a Razorpay error), so show the latest state.
      load();
      throw err;
    } finally {
      setBusy('');
    }
  };
  const quiet = (promise) => promise.catch(() => {});

  // Sends the payment link straight to the CUSTOMER in their WhatsApp chat,
  // through the chat's own send route (POST /api/customers/:number/
  // whatsapp-messages), so it also appears in the WhatsApp page thread. It's
  // a normal chat message, so WhatsApp only delivers it within 24h of the
  // customer's last message. The automatic international_payment_link
  // template goes to staff, not the customer, so this doesn't duplicate it.
  const sendLinkOnWhatsApp = async () => {
    if (!window.confirm(`Send the payment link to ${order.customer_name} (${order.customer_number}) on WhatsApp?`)) return;
    const firstName = (order.customer_name || '').split(' ')[0];
    const text = `Hi ${firstName}, here is the payment link for your order ${order.order_number}: *${formatMoney(order.final_total_paise, order.currency)}* (products ${formatMoney(order.product_subtotal_paise, order.currency)} + international shipping ${formatMoney(customerShippingPaise(order.shipping_charge_paise), order.currency)}${order.discount_paise > 0 ? ` - discount ${formatMoney(order.discount_paise, order.currency)}` : ''}).\n${order.payment_link_url}`;
    setBusy('whatsapp');
    setActionError('');
    setNotice('');
    try {
      await readJson(
        await authFetch(`/api/customers/${encodeURIComponent(order.customer_number)}/whatsapp-messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ body: text }),
        }),
        'Could not send the WhatsApp message.'
      );
      setNotice(`Payment link sent to ${order.customer_name} on WhatsApp.`);
    } catch (err) {
      setActionError(`${err.message} If the customer hasn't messaged in the last 24 hours, WhatsApp won't deliver a normal message: copy the link and send it from the WhatsApp page instead.`);
    } finally {
      setBusy('');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(order.payment_link_url);
      setNotice('Payment link copied.');
    } catch {
      setActionError('Could not copy. Select the link and copy it manually.');
    }
  };

  if (loading || !order) {
    return (
      <div className="modal-overlay modal-overlay-fullpage">
        <div className="modal-panel modal-panel-fullpage">
          <div className="modal-header">
            <h2>International Order</h2>
            <button className="modal-close" onClick={onClose} aria-label="Close"><CloseIcon width={18} height={18} /></button>
          </div>
          <div className="modal-body">
            {error ? <div className="call-feedback call-feedback-error">{error}</div> : <div className="loading-state">Loading…</div>}
          </div>
        </div>
      </div>
    );
  }

  const step = nextStep(order, { erpPacking });
  const s = order.status;
  const preVerifiedEdit = ['DRAFT', 'LOGISTICS_PENDING', 'LOGISTICS_ACCEPTED', 'ADDRESS_VERIFIED', 'PAYMENT_PENDING'].includes(s);
  const editableCustomer = preVerifiedEdit
    ? caps.create || caps.logistics
    : ['PAYMENT_CONFIRMED', 'PACKING', 'READY_FOR_DISPATCH'].includes(s) && caps.logistics;
  // In PAYMENT_PENDING (unpaid) saving products cancels the link; the server
  // moves the order back to "Generate Payment Link".
  const editableItems = caps.create && (['DRAFT', 'LOGISTICS_PENDING', 'LOGISTICS_ACCEPTED', 'ADDRESS_VERIFIED'].includes(s)
    || (s === 'PAYMENT_PENDING' && !['paid', 'partially_paid'].includes(order.payment_status)));
  const canCancel = !['SHIPPED', 'DELIVERED', 'CANCELLED'].includes(s)
    && (caps.cancel || (s === 'DRAFT' && order.created_by_id === user?.id));
  const moneyIn = ['paid', 'partially_paid'].includes(order.payment_status);
  const linkLive = s === 'PAYMENT_PENDING' && order.payment_link_url && order.payment_status !== 'unpaid';
  // After payment confirmation (e.g. an admin confirmed without payment),
  // admins can still record that the money came in through the backend.
  // While the order waits for payment the stage panel has the button.
  const canMarkPaidLater = caps.admin && order.payment_status !== 'paid'
    && ['PAYMENT_CONFIRMED', 'PACKING', 'READY_FOR_DISPATCH', 'SHIPPED', 'DELIVERED'].includes(s);

  // Admin-only hard delete; a paid order only for a superadmin who types its number.
  const handleDelete = async () => {
    const body = confirmOrderDelete(order);
    if (!body) return;
    setBusy('delete');
    setActionError('');
    try {
      await readJson(await authFetch(`/api/international-orders/${orderId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }), 'Could not delete this order.');
      onDeleted?.(order);
    } catch (err) {
      setActionError(err.message);
      setBusy('');
    }
  };

  // One-click actions shown in the "next step" banner.
  const bannerActions = (() => {
    if (s === 'DRAFT' && caps.create) {
      return (
        <button type="button" className="btn btn-primary" disabled={Boolean(busy)}
          onClick={() => quiet(run('submit', '/submit', {
            success: 'Order created successfully. Waiting for logistics to verify the address and add shipping charges.',
          }))}>
          {busy === 'submit' ? 'Sending…' : 'Send to logistics'}
        </button>
      );
    }
    if (s === 'LOGISTICS_PENDING' && caps.logistics) {
      return (
        <button type="button" className="btn btn-primary" disabled={Boolean(busy)}
          onClick={() => quiet(run('accept', '/accept', { success: 'Order accepted. Check and verify the shipping address.' }))}>
          {busy === 'accept' ? 'Accepting…' : 'Accept Order'}
        </button>
      );
    }
    if (s === 'PAYMENT_CONFIRMED' && caps.packing && !erpPacking) {
      return (
        <button type="button" className="btn btn-primary" disabled={Boolean(busy)}
          onClick={() => quiet(run('pack-start', '/start-packing', { success: 'Packing started.' }))}>
          {busy === 'pack-start' ? 'Starting…' : 'Start Packing'}
        </button>
      );
    }
    if (s === 'READY_FOR_DISPATCH' && caps.logistics && panel !== 'dispatch') {
      return <button type="button" className="btn btn-primary" onClick={() => setPanel('dispatch')}>Dispatch</button>;
    }
    if (s === 'SHIPPED') {
      return (
        <>
          {order.shipment?.tracking_url && (
            <a className="btn btn-primary" href={order.shipment.tracking_url} target="_blank" rel="noopener noreferrer">Track Shipment</a>
          )}
          {caps.logistics && panel !== 'tracking' && (
            <button type="button" className="btn btn-secondary" onClick={() => setPanel('tracking')}>
              Change courier / AWB
            </button>
          )}
          {caps.logistics && (
            <button type="button" className="btn btn-secondary" disabled={Boolean(busy)}
              onClick={() => quiet(run('deliver', '/deliver', { success: 'Marked delivered.' }))}>
              {busy === 'deliver' ? 'Saving…' : 'Mark delivered'}
            </button>
          )}
        </>
      );
    }
    if (s === 'DELIVERED' && order.shipment?.tracking_url) {
      return <a className="btn btn-secondary" href={order.shipment.tracking_url} target="_blank" rel="noopener noreferrer">Track Shipment</a>;
    }
    return null;
  })();

  // The workspace for the current stage, shown above the full record.
  const stagePanel = (() => {
    if (s === 'LOGISTICS_ACCEPTED') {
      return (
        <div className="intl-stage">
          <AddressBlock order={order} title="Shipping address to verify" />
          {caps.logistics ? (
            <div className="intl-stage-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setDialog('customer')}>Edit / correct address</button>
              <button type="button" className="btn btn-primary" disabled={Boolean(busy)}
                onClick={() => quiet(run('verify', '/verify-address', { success: 'Address verified. Add the courier charge next.' }))}>
                {busy === 'verify' ? 'Verifying…' : 'Verify Address'}
              </button>
            </div>
          ) : <p className="intl-muted">Waiting for logistics to verify this address.</p>}
        </div>
      );
    }
    // Logistics saves the courier charge; the order team checks the total,
    // adds any extra item and generates the payment link. Admins see both.
    if (s === 'ADDRESS_VERIFIED' && (caps.logistics || caps.create)) {
      // A re-check request (customer didn't pay for 2+ days) blocks a new
      // link until logistics saves the charge again; the server checks too.
      const recheck = Boolean(order.courier_recheck_requested_at);
      const charged = order.shipping_charge_paise !== null && !recheck;
      return (
        <div className="intl-stage">
          {recheck && (
            <div className="intl-stale-alert" role="status">
              <div>
                <strong>Re-check the courier charge.</strong>{' '}
                The customer didn't pay the last payment link, so {order.courier_recheck_requested_by || 'the order team'} sent
                this back on {formatDateTime(order.courier_recheck_requested_at)}.
                Last quoted charge: {formatMoney(order.shipping_charge_paise, order.currency)}.
                {caps.logistics
                  ? " Check today's rate and save it, even if it's the same."
                  : ' Waiting for logistics to confirm it before a new link can be generated.'}
              </div>
            </div>
          )}
          {caps.logistics && (
            <>
              <h3 className="intl-section-title">Courier charge</h3>
              <LogisticsForm
                order={order}
                quote
                onSave={(body) => run('logistics', '/logistics', {
                  method: 'PATCH',
                  inline: true,
                  body,
                  success: 'Saved. The order team will generate the payment link.',
                })}
              />
            </>
          )}
          {caps.create && (
            <div className={caps.logistics ? 'intl-stage-split' : undefined}>
              <h3 className="intl-section-title">Payment link</h3>
              <div className="intl-totals intl-totals-preview">
                <div><span>Product total</span><span>{formatMoney(order.product_subtotal_paise, order.currency)}</span></div>
                <div>
                  <span>Courier charge{charged ? ` (${formatMoney(order.shipping_charge_paise, order.currency)} + ${COURIER_MARKUP_PERCENT}%)` : ''}</span>
                  <span>{charged ? formatMoney(customerShippingPaise(order.shipping_charge_paise), order.currency) : <span className="intl-muted">Waiting for logistics</span>}</span>
                </div>
                {order.discount_paise > 0 && (
                  <div><span>Discount</span><span>- {formatMoney(order.discount_paise, order.currency)}</span></div>
                )}
                <div className="intl-totals-final">
                  <span>Final total</span>
                  <span>{charged ? formatMoney(order.final_total_paise, order.currency) : '-'}</span>
                </div>
              </div>
              <div className="intl-stage-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setDialog('items')}>
                  Edit products / add extra item
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setDialog('discount')}>
                  Edit price / discount
                </button>
                <button type="button" className="btn btn-primary" disabled={Boolean(busy) || !charged}
                  title={!charged ? 'Logistics has to save the courier charge first' : undefined}
                  onClick={() => quiet(run('link', '/payment-link', { success: 'Payment link generated. Send it to the customer.' }))}>
                  {busy === 'link' ? 'Generating link…' : 'Generate Payment Link'}
                </button>
              </div>
              <p className="intl-muted intl-form-hint">
                {charged
                  ? 'Check the products first. If you are sending something that isn\'t in the ERP, add it as an extra item so it\'s in the total. The link is for the final total and moves the order to Payment Pending.'
                  : recheck
                    ? 'Logistics hasn\'t re-checked the courier charge yet. You can still add or change products now.'
                    : 'Logistics hasn\'t saved the courier charge yet. You can still add or change products now.'}
              </p>
            </div>
          )}
        </div>
      );
    }
    if (s === 'PAYMENT_PENDING') {
      const unpaidDays = staleUnpaidDays(order);
      return (
        <div className="intl-stage">
          {unpaidDays > 0 && (
            <div className="intl-stale-alert" role="status">
              <div>
                <strong>Not paid in {unpaidDays} days.</strong>{' '}
                The link was generated on {formatDateTime(order.payment_link_created_at)} with a courier charge
                of {formatMoney(order.shipping_charge_paise, order.currency)}. Courier rates may have changed, so ask
                logistics to re-check it before the customer pays.
              </div>
              {(caps.create || caps.logistics) && (
                <button type="button" className="btn btn-primary btn-small" disabled={Boolean(busy)} onClick={() => setDialog('recheck')}>
                  Ask logistics to re-check courier charge
                </button>
              )}
            </div>
          )}
          <div className="intl-stage-row">
            <div>
              <div className="intl-shipto-title">Payment status</div>
              <StatusPill meta={PAYMENT_META[order.payment_status]} />
              <span className="intl-stage-amount">{formatMoney(order.final_total_paise, order.currency)}</span>
            </div>
            <button type="button" className="btn btn-secondary btn-small" disabled={Boolean(busy) || !order.payment_link_id}
              onClick={() => quiet(run('refresh', '/payment/refresh', {
                success: (o) => (o.status === 'PAYMENT_CONFIRMED' ? 'Payment received. Order is now Payment Confirmed.' : `Razorpay status: ${o.payment_link_status || 'unchanged'}.`),
              }))}>
              <RefreshIcon width={14} height={14} /> {busy === 'refresh' ? 'Checking…' : 'Check payment now'}
            </button>
          </div>
          {linkLive ? (
            <div className="intl-link-row">
              <a href={order.payment_link_url} target="_blank" rel="noopener noreferrer">{order.payment_link_url}</a>
              <div className="cod-actions">
                <a className="btn btn-secondary btn-small" href={order.payment_link_url} target="_blank" rel="noopener noreferrer">Open Payment Link</a>
                <button type="button" className="btn btn-secondary btn-small" onClick={copyLink}>Copy Payment Link</button>
                {(caps.create || caps.logistics) && (
                  <button type="button" className="btn btn-primary btn-small" disabled={Boolean(busy)} onClick={sendLinkOnWhatsApp}>
                    {busy === 'whatsapp' ? 'Sending…' : 'Send on WhatsApp'}
                  </button>
                )}
              </div>
            </div>
          ) : (caps.create || caps.logistics) && (
            <div className="intl-stage-actions">
              <button type="button" className="btn btn-primary" disabled={Boolean(busy)}
                onClick={() => quiet(run('link', '/payment-link', { success: 'New payment link generated.' }))}>
                {busy === 'link' ? 'Generating…' : 'Generate new payment link'}
              </button>
            </div>
          )}
          <div className="cod-actions intl-section-actions">
            {editableItems && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setDialog('items')}>
                Edit products
              </button>
            )}
            {(caps.create || caps.logistics) && order.payment_status !== 'partially_paid' && order.payment_status !== 'paid' && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setDialog('reopen')}>
                Send back to logistics
              </button>
            )}
            {caps.admin && order.payment_status !== 'paid' && (
              <button type="button" className="btn btn-primary btn-small" onClick={() => setDialog('markPaid')}>
                Mark payment received (backend)
              </button>
            )}
            {caps.admin && (
              <button type="button" className="btn btn-secondary btn-small" onClick={() => setDialog('confirm')}>
                Confirm without online payment
              </button>
            )}
          </div>
        </div>
      );
    }
    // With ERP packing on, packing happens in the ERP (Packaging >
    // International): claim, photo, Pack, which moves the order to Ready for
    // Dispatch here. Until then, the CRM's own checklist.
    if (erpPacking && (s === 'PACKING' || s === 'PAYMENT_CONFIRMED')) {
      return (
        <div className="intl-stage">
          <h3 className="intl-section-title">Packing list</h3>
          <p className="intl-muted">Packed in the ERP, on Packaging &gt; International. This order moves to Ready for Dispatch once it's packed there.</p>
          <PackingChecklist order={order} canPack={false} />
        </div>
      );
    }
    if (s === 'PACKING' || (s === 'PAYMENT_CONFIRMED' && caps.packing)) {
      return (
        <div className="intl-stage">
          <h3 className="intl-section-title">Packing list</h3>
          {s === 'PACKING' ? (
            <PackingChecklist
              order={order}
              canPack={caps.packing}
              busy={busy === 'packed'}
              onPacked={(ids) => quiet(run('packed', '/packed', {
                body: { packed_item_ids: ids },
                success: 'Order packed. It is now Ready for Dispatch.',
              }))}
            />
          ) : (
            <p className="intl-muted">Start packing to tick off each product.</p>
          )}
        </div>
      );
    }
    // Logistics can still switch courier or correct the AWB after dispatch.
    // Every change is logged with the old and new values.
    if (s === 'SHIPPED' && panel === 'tracking' && caps.logistics) {
      return (
        <div className="intl-stage">
          <div className="intl-dispatch">
            <div>
              <AddressBlock order={order} title="Ship to" />
              {order.shipment && (
                <dl className="intl-fields intl-current-shipment">
                  <Field label="Current courier">{order.shipment.courier_name}</Field>
                  <Field label="Current AWB">{order.shipment.awb_number}</Field>
                </dl>
              )}
            </div>
            <div className="intl-dispatch-form">
              <h3 className="intl-section-title">Change courier / AWB</h3>
              <DispatchForm
                order={order}
                onCancel={() => setPanel(null)}
                onSave={async (body) => {
                  await run('tracking', '/shipment', { inline: true, method: 'PUT', body, success: 'Courier / AWB updated.' });
                  setPanel(null);
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    if (s === 'READY_FOR_DISPATCH' && panel === 'dispatch') {
      return (
        <div className="intl-stage">
          <div className="intl-dispatch">
            <AddressBlock order={order} title="Ship to (verified address)" />
            <div className="intl-dispatch-form">
              <h3 className="intl-section-title">Dispatch details</h3>
              <DispatchForm
                order={order}
                onCancel={() => setPanel(null)}
                onSave={async (body) => {
                  await run('dispatch', '/shipment', { inline: true, method: 'PUT', body, success: 'Dispatched. The order is now Shipped.' });
                  setPanel(null);
                }}
              />
            </div>
          </div>
        </div>
      );
    }
    return null;
  })();

  return (
    <div className="modal-overlay modal-overlay-fullpage">
      <div className="modal-panel modal-panel-fullpage intl-detail">
        <div className="modal-header intl-detail-header">
          <div className="intl-detail-title">
            <p className="order-query-detail-eyebrow">International Order</p>
            <h2>
              {order.order_number} <StatusPill meta={STATUS_META[s]} />
            </h2>
            <p className="modal-subtitle">
              Customer: {order.customer_name} · Country: {order.country || 'not set'} · WhatsApp {order.customer_number}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => downloadOrderPdf(order)}>
              <DownloadIcon width={16} height={16} /> Download PDF
            </button>
            <button className="modal-close" onClick={onClose} aria-label="Close"><CloseIcon width={18} height={18} /></button>
          </div>
        </div>

        <div className="modal-body">
          <ProgressBar status={s} />

          <div className={`intl-next intl-next-${step.tone}`}>
            <div>
              {step.who && <span className="intl-next-who">Next: {step.who}</span>}
              <span>{step.text}</span>
            </div>
            {bannerActions && <div className="intl-next-actions">{bannerActions}</div>}
          </div>
          {notice && <div className="call-feedback call-feedback-ok intl-feedback">{notice}</div>}
          {actionError && <div className="call-feedback call-feedback-error intl-feedback">{actionError}</div>}

          {stagePanel}

          <div className="intl-detail-grid">
            <div>
              <Section
                title="Customer & shipping address"
                action={editableCustomer && <button type="button" className="link-btn" onClick={() => setDialog('customer')}>Edit</button>}
              >
                <dl className="intl-fields">
                  <Field label="Customer">{order.customer_name}</Field>
                  <Field label="WhatsApp">{order.customer_number}</Field>
                  <Field label="Email">{order.email}</Field>
                  <Field label="Country">{order.country}</Field>
                </dl>
                <div className="intl-address">
                  {addressLines(order).length ? addressLines(order).map((line) => <div key={line}>{line}</div>) : <span className="intl-muted">No address yet</span>}
                </div>
                {order.customer_notes && <p className="intl-note"><strong>Customer notes:</strong> {order.customer_notes}</p>}
              </Section>

              <Section
                title={`Products (${order.items.length})`}
                action={editableItems && <button type="button" className="link-btn" onClick={() => setDialog('items')}>Edit products</button>}
              >
                {order.items.length ? (
                  <table className="intl-lines">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th className="intl-num">Qty</th>
                        <th className="intl-num">Unit price</th>
                        <th className="intl-num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.items.map((item) => (
                        <tr key={item.id}>
                          <td>
                            <div className="intl-line-product">
                              {item.image_url ? <img src={item.image_url} alt="" className="intl-thumb" /> : <span className="intl-thumb" />}
                              <span>
                                {item.name}
                                {isCustomItem(item) && <span className="intl-pill intl-pill-neutral intl-extra-pill">Extra item</span>}
                              </span>
                            </div>
                          </td>
                          <td>{item.sku || '-'}</td>
                          <td className="intl-num">{formatQty(item.qty)} {qtyUnit(item)}</td>
                          <td className="intl-num">{formatMoney(item.unit_price_paise, order.currency)}</td>
                          <td className="intl-num">{formatMoney(item.line_total_paise, order.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="intl-empty">No products yet.</div>
                )}
              </Section>

              <Section title="Pricing">
                <div className="intl-totals">
                  <div><span>Product subtotal</span><span>{formatMoney(order.product_subtotal_paise, order.currency)}</span></div>
                  <div>
                    <span>
                      International shipping{order.courier_service ? ` (${order.courier_service})` : ''}
                      {order.shipping_charge_paise !== null && ` · ${formatMoney(order.shipping_charge_paise, order.currency)} + ${COURIER_MARKUP_PERCENT}%`}
                    </span>
                    <span>{order.shipping_charge_paise === null ? <span className="intl-muted">Not added yet</span> : formatMoney(customerShippingPaise(order.shipping_charge_paise), order.currency)}</span>
                  </div>
                  {order.discount_paise > 0 && (
                    <div>
                      <span>Discount{order.discount_reason ? ` (${order.discount_reason})` : ''}</span>
                      <span>- {formatMoney(order.discount_paise, order.currency)}</span>
                    </div>
                  )}
                  <div className="intl-totals-final">
                    <span>Final total</span>
                    <span>{order.final_total_paise === null ? <span className="intl-muted">After courier charge</span> : formatMoney(order.final_total_paise, order.currency)}</span>
                  </div>
                </div>
                <p className="intl-muted">
                  Prices are the ERP selling prices at the time each product was added
                  {order.items.some(isCustomItem) ? ', except extra items, which were priced by the order team' : ''}. ERP stock is never changed by this order.
                </p>
              </Section>
            </div>

            <div>
              <Section
                title="Logistics"
                action={caps.logistics && ['PAYMENT_CONFIRMED', 'PACKING', 'READY_FOR_DISPATCH'].includes(s) && panel !== 'logistics' && (
                  <button type="button" className="link-btn" onClick={() => setPanel('logistics')}>Edit notes</button>
                )}
              >
                {panel === 'logistics' ? (
                  <LogisticsForm
                    order={order}
                    quote={false}
                    onCancel={() => setPanel(null)}
                    onSave={async (body) => {
                      await run('logistics', '/logistics', { method: 'PATCH', inline: true, body, success: 'Logistics details saved.' });
                      setPanel(null);
                    }}
                  />
                ) : (
                  <dl className="intl-fields">
                    <Field label="Accepted">{byLine(order.accepted_by, order.accepted_at)}</Field>
                    <Field label="Address"><StatusPill meta={ADDRESS_META[order.address_status] || ADDRESS_META.pending} /></Field>
                    <Field label="Verified">{byLine(order.address_verified_by, order.address_verified_at)}</Field>
                    <Field label="Courier / service">{order.courier_service}</Field>
                    <Field label="Courier charge">{order.shipping_charge_paise === null ? null : formatMoney(order.shipping_charge_paise, order.currency)}</Field>
                    <Field label="Package weight">{order.package_weight_grams ? `${order.package_weight_grams / 1000} kg` : null}</Field>
                    <Field label="Dimensions">
                      {[order.package_length_cm, order.package_width_cm, order.package_height_cm].some((v) => v)
                        ? `${[order.package_length_cm, order.package_width_cm, order.package_height_cm].map((v) => v || '–').join(' × ')} cm`
                        : null}
                    </Field>
                    {order.logistics_notes && <Field label="Notes">{order.logistics_notes}</Field>}
                  </dl>
                )}
              </Section>

              <Section
                title="Payment"
                action={canMarkPaidLater && (
                  <button type="button" className="link-btn" onClick={() => setDialog('markPaid')}>Mark payment received</button>
                )}
              >
                <dl className="intl-fields">
                  <Field label="Status"><StatusPill meta={PAYMENT_META[order.payment_status]} /></Field>
                  <Field label="Amount due">{order.final_total_paise === null ? null : formatMoney(order.final_total_paise, order.currency)}</Field>
                  {order.payment_amount_paid_paise > 0 && (
                    <Field label="Paid">{formatMoney(order.payment_amount_paid_paise, order.payment_currency || order.currency)}</Field>
                  )}
                  {order.payment_id && <Field label="Payment ID">{order.payment_id}</Field>}
                  {order.payment_paid_at && <Field label="Paid on">{formatDateTime(order.payment_paid_at)}</Field>}
                  {order.payment_link_url && s !== 'PAYMENT_PENDING' && (
                    <Field label="Payment link"><a href={order.payment_link_url} target="_blank" rel="noopener noreferrer">Open</a></Field>
                  )}
                  {order.payment_method === 'backend' && (
                    <>
                      <Field label="Received via">Backend payment (marked by {order.payment_marked_by || 'admin'})</Field>
                      <Field label="Reference">{order.payment_reference}</Field>
                    </>
                  )}
                  {order.confirmed_without_payment ? <Field label="Note">Confirmed by an admin without a verified payment</Field> : null}
                </dl>
              </Section>

              <Section title="Packing">
                <dl className="intl-fields">
                  <Field label="Started">{byLine(order.packing_started_by, order.packing_started_at)}</Field>
                  <Field label="Packed">{byLine(order.packed_by, order.packed_at)}</Field>
                  {order.pack_image_url && (
                    <Field label="Parcel photo">
                      <a href={order.pack_image_url} target="_blank" rel="noopener noreferrer">
                        <img src={order.pack_image_url} alt="Packed parcel" className="intl-thumb" />
                      </a>
                    </Field>
                  )}
                </dl>
              </Section>

              <Section
                title="Shipment"
                action={caps.logistics && s === 'SHIPPED' && panel !== 'tracking' && (
                  <button type="button" className="link-btn" onClick={() => setPanel('tracking')}>Change courier / AWB</button>
                )}
              >
                {order.shipment ? (
                  <>
                    <dl className="intl-fields">
                      <Field label="Courier">{order.shipment.courier_name}</Field>
                      <Field label="AWB">{order.shipment.awb_number}</Field>
                      <Field label="Dispatch date">{formatDate(order.shipment.shipment_date)}</Field>
                      <Field label="Dispatched">{byLine(order.dispatched_by, order.dispatched_at)}</Field>
                      {order.shipment.notes && <Field label="Notes">{order.shipment.notes}</Field>}
                    </dl>
                    {order.shipment.tracking_url && (
                      <a className="btn btn-primary btn-small intl-track" href={order.shipment.tracking_url} target="_blank" rel="noopener noreferrer">
                        Track Shipment
                      </a>
                    )}
                  </>
                ) : (
                  <div className="intl-empty">Added at dispatch, after packing.</div>
                )}
              </Section>

              <WhatsAppMessages
                order={order}
                canAct={caps.create || caps.logistics}
                busy={busy}
                onRetry={(notification) => quiet(run(`retry-${notification.id}`, `/notifications/${notification.id}/retry`, {
                  success: 'Message queued again.',
                }))}
                onSendUpdate={() => quiet(run('wa-update', '/notifications/updated-amount', {
                  success: 'Updated amount queued for WhatsApp.',
                }))}
              />

              <Section title="Activity">
                <ol className="intl-activity">
                  {events.map((event) => {
                    const { title, detail } = describeEvent(event, order.currency);
                    return (
                      <li key={event.id}>
                        <div className="intl-activity-top">
                          <span className="intl-activity-title">{title}</span>
                          <span className="intl-muted">{formatDateTime(event.created_at)}</span>
                        </div>
                        <div className="intl-muted">
                          {event.actor_username}
                          {detail ? ` · ${detail}` : ''}
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </Section>

              {(canCancel || caps.admin) && (
                <div className="intl-danger-zone cod-actions">
                  {canCancel && (
                    <button type="button" className="btn btn-danger-outline btn-small" onClick={() => setDialog('cancel')}>
                      Cancel order
                    </button>
                  )}
                  {caps.admin && (
                    <button
                      type="button"
                      className="btn btn-danger-outline btn-small"
                      disabled={(moneyIn && !caps.superadmin) || Boolean(busy)}
                      title={moneyIn ? (caps.superadmin ? 'Delete paid test order (type the order number to confirm)' : 'Paid orders can only be cancelled, not deleted') : undefined}
                      onClick={handleDelete}
                    >
                      {busy === 'delete' ? 'Deleting…' : 'Delete order'}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {dialog === 'customer' && (
        <EditCustomerDialog order={order} onClose={() => setDialog(null)}
          onSave={async (body) => {
            await run('customer', '/customer', {
              method: 'PATCH',
              inline: true,
              body,
              success: (o) => (o.status !== order.status ? 'Address changed. It needs to be verified again.' : 'Customer details saved.'),
            });
            setDialog(null);
          }} />
      )}
      {dialog === 'items' && (
        <EditItemsDialog order={order} onClose={() => setDialog(null)}
          onSave={async (items) => {
            await run('items', '/items', {
              inline: true, method: 'PUT', body: { items },
              success: (o) => (o.status === 'ADDRESS_VERIFIED' && s === 'PAYMENT_PENDING'
                ? 'Products updated. The old payment link was cancelled. Generate a new one for the new total.'
                : 'Products updated.'),
            });
            setDialog(null);
          }} />
      )}
      {dialog === 'reopen' && (
        <ReasonDialog title="Send back to logistics" subtitle="Use this to change products or the courier charge. The unpaid payment link is cancelled."
          label="Reason" submitLabel="Send back" optional onClose={() => setDialog(null)}
          onSave={async (reason) => {
            await run('reopen', '/reopen', { inline: true, body: { reason }, success: 'Sent back to logistics.' });
            setDialog(null);
          }} />
      )}
      {dialog === 'recheck' && (
        <ReasonDialog title="Re-check courier charge"
          subtitle="The unpaid payment link is cancelled and the order goes back to logistics. They confirm or update the courier charge, then you generate a new link and send it to the customer."
          label="Note for logistics" submitLabel="Send to logistics" optional onClose={() => setDialog(null)}
          onSave={async (reason) => {
            await run('reopen', '/reopen', {
              inline: true,
              body: { reason, recheck_courier_charge: true },
              success: 'Sent back to logistics to re-check the courier charge. The old payment link was cancelled.',
            });
            setDialog(null);
          }} />
      )}
      {dialog === 'confirm' && (
        <ReasonDialog title="Confirm without online payment"
          subtitle="Payment will stay marked as not received. Use this only when the customer paid outside Razorpay, and say how."
          label="Reason (e.g. bank transfer reference)" submitLabel="Confirm payment" onClose={() => setDialog(null)}
          onSave={async (overrideReason) => {
            await run('confirm', '/confirm', { inline: true, body: { overrideReason }, success: 'Order moved to Payment Confirmed.' });
            setDialog(null);
          }} />
      )}
      {dialog === 'discount' && (
        <DiscountDialog order={order} onClose={() => setDialog(null)}
          onSave={async (body) => {
            await run('discount', '/discount', {
              inline: true,
              method: 'PUT',
              body,
              success: (o) => (o.discount_paise > 0 ? 'Discount saved. Check the final total, then generate the payment link.' : 'Discount removed.'),
            });
            setDialog(null);
          }} />
      )}
      {dialog === 'markPaid' && (
        <MarkPaidDialog order={order} onClose={() => setDialog(null)}
          onSave={async (body) => {
            await run('markPaid', '/payment/mark-received', {
              inline: true,
              body,
              success: (o) => (o.status !== s ? 'Payment received. Order moved to Payment Confirmed.' : 'Payment marked as received.'),
            });
            setDialog(null);
          }} />
      )}
      {dialog === 'cancel' && (
        <ReasonDialog title={`Cancel ${order.order_number}`}
          subtitle={moneyIn ? 'The customer has paid. Cancelling does not refund them: issue the refund in Razorpay separately.' : 'Any unpaid payment link is cancelled too.'}
          label="Reason" submitLabel="Cancel order" danger onClose={() => setDialog(null)}
          onSave={async (reason) => {
            await run('cancel', '/cancel', { inline: true, body: { reason }, success: 'Order cancelled.' });
            setDialog(null);
          }} />
      )}
    </div>
  );
}

const WA_EVENTS = {
  INTERNATIONAL_ORDER_CREATED: 'Order created message',
  INTERNATIONAL_SHIPPING_CHARGE_ADDED: 'Shipping charge message',
  INTERNATIONAL_SHIPPING_CHARGE_UPDATED: 'Updated amount message',
  INTERNATIONAL_PAYMENT_LINK_SENT: 'Payment link message',
  INTERNATIONAL_STAFF_NEW_ORDER: 'Logistics alert',
};
// Who each message goes to (server/intlOrders/notifications.js), and what
// to show before / instead of sending.
const WA_RECIPIENTS = {
  INTERNATIONAL_ORDER_CREATED: 'to logistics',
  INTERNATIONAL_SHIPPING_CHARGE_ADDED: 'to the order team',
  INTERNATIONAL_SHIPPING_CHARGE_UPDATED: 'to the order team',
  INTERNATIONAL_PAYMENT_LINK_SENT: 'to the user who created this order',
};
const WA_NOT_SENT = {
  INTERNATIONAL_ORDER_CREATED: 'Not sent yet. Goes out when the order is sent to logistics.',
  INTERNATIONAL_SHIPPING_CHARGE_ADDED: 'Not sent yet. Goes out when logistics adds the courier charge.',
  INTERNATIONAL_PAYMENT_LINK_SENT: 'Not sent yet. Goes out when the payment link is generated.',
};
// The step has happened but no one could be messaged.
const waNobody = (event, order) => {
  if (event === 'INTERNATIONAL_ORDER_CREATED' && !['DRAFT', 'CANCELLED'].includes(order.status)) {
    return 'Not sent: no logistics user has a mobile number in Manage Admins.';
  }
  if (event === 'INTERNATIONAL_SHIPPING_CHARGE_ADDED' && order.shipping_charge_paise !== null) {
    return 'Not sent: no order-team user has a mobile number in Manage Admins.';
  }
  if (event === 'INTERNATIONAL_PAYMENT_LINK_SENT' && order.payment_link_url) {
    return `Not sent: ${order.created_by || 'the user who created this order'} has no mobile number in Manage Admins.`;
  }
  return null;
};
const WA_STATUS = {
  sent: { icon: '✓', label: 'Sent', tone: 'success' },
  pending: { icon: '…', label: 'Sending', tone: 'neutral' },
  sending: { icon: '…', label: 'Sending', tone: 'neutral' },
  failed: { icon: '✕', label: 'Failed', tone: 'danger' },
};
const SHIPPING_EVENTS = ['INTERNATIONAL_SHIPPING_CHARGE_ADDED', 'INTERNATIONAL_SHIPPING_CHARGE_UPDATED'];

// Automatic AiSensy WhatsApp messages for this order (server/intlOrders/
// notifications.js). They go to International staff (Manage Admins mobile
// numbers), not the customer. One block per message, listing each person
// it went to; the three main messages always show, "not sent yet" until
// their step happens.
function WhatsAppMessages({ order, canAct, busy, onRetry, onSendUpdate }) {
  const notifications = order.notifications || [];
  const byEvent = (event) => notifications.filter((n) => n.event_type === event);
  const blocks = [
    'INTERNATIONAL_ORDER_CREATED',
    'INTERNATIONAL_SHIPPING_CHARGE_ADDED',
    'INTERNATIONAL_SHIPPING_CHARGE_UPDATED',
    'INTERNATIONAL_PAYMENT_LINK_SENT',
    'INTERNATIONAL_STAFF_NEW_ORDER',
  ].filter((event) => WA_NOT_SENT[event] || byEvent(event).length);

  // Offer the updated amount only if the last amount sent differs from now.
  const lastShipping = [...notifications].reverse()
    .find((n) => n.status === 'sent' && SHIPPING_EVENTS.includes(n.event_type));
  const amountChanged = lastShipping && order.final_total_paise !== null
    && (lastShipping.variables?.final_total_paise !== order.final_total_paise
      || lastShipping.variables?.shipping_charge_paise !== order.shipping_charge_paise);
  const canSendUpdate = canAct && amountChanged && ['ADDRESS_VERIFIED', 'PAYMENT_PENDING'].includes(order.status);

  return (
    <Section title="WhatsApp messages">
      <p className="intl-muted intl-wa-note">Sent to staff mobile numbers in Manage Admins, not to the customer.</p>
      {blocks.map((event) => {
        const rows = byEvent(event);
        return (
          <div key={event} className="intl-wa-block">
            <div className="intl-wa-title">
              {WA_EVENTS[event] || event}
              {WA_RECIPIENTS[event] && <span className="intl-muted"> · {WA_RECIPIENTS[event]}</span>}
            </div>
            {rows.length === 0 ? (
              <div className="intl-muted">{waNobody(event, order) || WA_NOT_SENT[event]}</div>
            ) : (
              <ul className="intl-wa-list">
                {rows.map((n) => {
                  const meta = WA_STATUS[n.status];
                  const who = n.recipient_type === 'staff' ? n.customer_name : 'Customer';
                  return (
                    <li key={n.id} className={`intl-wa-${meta ? meta.tone : 'none'}`}>
                      <span className="intl-wa-icon">{meta ? meta.icon : '○'}</span>
                      <div className="intl-wa-body">
                        <div>{who} <span className="intl-muted">{n.destination}</span></div>
                        <div className="intl-muted">
                          {n.status === 'sent' && `Sent ${formatDateTime(n.sent_at)}${
                            SHIPPING_EVENTS.includes(n.event_type) && n.variables?.final_total ? ` · total ${n.variables.final_total}` : ''}`}
                          {(n.status === 'pending' || n.status === 'sending') && 'Sending…'}
                          {n.status === 'failed' && `Failed: ${n.last_error || 'unknown error'}`}
                        </div>
                      </div>
                      {n.status === 'failed' && canAct && (
                        <button type="button" className="btn btn-secondary btn-small" disabled={Boolean(busy)} onClick={() => onRetry(n)}>
                          {busy === `retry-${n.id}` ? 'Retrying…' : 'Retry'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
      {canSendUpdate && (
        <div className="intl-wa-update">
          <span className="intl-muted">
            Last amount sent: {lastShipping.variables.final_total}; the total is now {formatMoney(order.final_total_paise, order.currency)}.
          </span>
          <button type="button" className="btn btn-secondary btn-small" disabled={Boolean(busy)} onClick={onSendUpdate}>
            {busy === 'wa-update' ? 'Sending…' : 'Send updated amount'}
          </button>
        </div>
      )}
    </Section>
  );
}


function EditCustomerDialog({ order, onClose, onSave }) {
  const [customer, setCustomer] = useState(customerFromOrder(order));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(customer);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  const subtitle = order.status === 'PAYMENT_PENDING'
    ? 'Changing the address cancels the current payment link and it has to be verified again.'
    : order.status === 'ADDRESS_VERIFIED'
    ? 'Changing the address means it has to be verified again.'
    : 'Address changes are recorded in the order activity.';
  return (
    <Dialog title="Customer & shipping address" subtitle={subtitle} wide onClose={onClose}>
      <form onSubmit={submit}>
        <CustomerFields value={customer} onChange={setCustomer} />
        {error && <div className="login-error">{error}</div>}
        <DialogActions onCancel={onClose} busy={busy} submitLabel="Save" />
      </form>
    </Dialog>
  );
}

function EditItemsDialog({ order, onClose, onSave }) {
  const [lines, setLines] = useState(linesFromItems(order.items));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(itemsPayload(lines));
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Dialog title="Products" subtitle="Existing lines keep the price they were added at. New products use the current ERP price. Something that isn't in the ERP can be added as an extra item with your own price." wide onClose={onClose}>
      <form onSubmit={submit}>
        {order.status === 'PAYMENT_PENDING' && (
          <div className="intl-stale-alert" role="status">
            <div>
              <strong>A payment link was already sent.</strong> Saving a change cancels it. Then generate a new
              payment link for the new total and send it to the customer.
            </div>
          </div>
        )}
        <ProductPicker lines={lines} onChange={setLines} currency={order.currency} />
        {error && <div className="login-error">{error}</div>}
        <DialogActions onCancel={onClose} busy={busy} submitLabel="Save products" disabled={order.status !== 'DRAFT' && !lines.length} />
      </form>
    </Dialog>
  );
}

function ReasonDialog({ title, subtitle, label, submitLabel, danger, optional, onClose, onSave }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave(reason.trim());
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Dialog title={title} subtitle={subtitle} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="login-field">
          <span>{label}{optional ? ' (optional)' : ' *'}</span>
          <textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
        </label>
        {error && <div className="login-error">{error}</div>}
        <DialogActions onCancel={onClose} busy={busy} submitLabel={submitLabel} danger={danger} disabled={!optional && !reason.trim()} />
      </form>
    </Dialog>
  );
}

// Order team: the price agreed with the customer. Type either the new final
// price (e.g. 13000) or the discount (e.g. 2000); the other is worked out.
// Only the discount is sent; the server recomputes the total.
function DiscountDialog({ order, onClose, onSave }) {
  const charge = order.shipping_charge_paise;
  const grossPaise = order.product_subtotal_paise + (customerShippingPaise(charge) || 0);
  const rupees = (paise) => String(Math.round(paise) / 100);
  const [discount, setDiscount] = useState(order.discount_paise > 0 ? rupees(order.discount_paise) : '');
  const [finalPrice, setFinalPrice] = useState(
    charge === null ? '' : rupees(grossPaise - (order.discount_paise || 0))
  );
  const [reason, setReason] = useState(order.discount_reason || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onDiscount = (value) => {
    setDiscount(value);
    if (charge !== null) setFinalPrice(value === '' ? rupees(grossPaise) : rupees(grossPaise - Math.round(Number(value) * 100)));
  };
  const onFinalPrice = (value) => {
    setFinalPrice(value);
    setDiscount(value === '' ? '' : rupees(grossPaise - Math.round(Number(value) * 100)));
  };

  const discountPaise = discount === '' ? 0 : Math.round(Number(discount) * 100);
  const invalid = Number.isNaN(discountPaise) || discountPaise < 0 || (discountPaise > 0 && discountPaise >= grossPaise);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave({ discount: discount.trim(), reason: reason.trim() });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Dialog title="Edit price / discount"
      subtitle={charge === null
        ? `Products total ${formatMoney(order.product_subtotal_paise, order.currency)}. The courier charge isn't added yet, so enter the discount.`
        : `Total before discount: ${formatMoney(grossPaise, order.currency)} (products + courier). The payment link will be for the final price.`}
      onClose={onClose}>
      <form onSubmit={submit}>
        <div className="intl-form-grid">
          {charge !== null && (
            <label className="login-field">
              <span>Final price ({order.currency})</span>
              <input type="number" min="0" step="0.01" value={finalPrice} onChange={(event) => onFinalPrice(event.target.value)} autoFocus />
            </label>
          )}
          <label className="login-field">
            <span>Discount ({order.currency})</span>
            <input type="number" min="0" step="0.01" value={discount} onChange={(event) => onDiscount(event.target.value)}
              autoFocus={charge === null} />
          </label>
        </div>
        <label className="login-field">
          <span>Reason (optional)</span>
          <input type="text" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. Agreed with the customer on call" />
        </label>
        {invalid && <div className="login-error">The discount has to be less than the total.</div>}
        {error && <div className="login-error">{error}</div>}
        <DialogActions onCancel={onClose} busy={busy} submitLabel={discountPaise > 0 ? 'Save discount' : 'Save (no discount)'} disabled={invalid} />
      </form>
    </Dialog>
  );
}

// Admin only: the customer paid outside Razorpay (logged on the Backend
// Payments page). Marks the payment received and cancels any live link.
function MarkPaidDialog({ order, onClose, onSave }) {
  const [amount, setAmount] = useState(order.final_total_paise === null ? '' : String(order.final_total_paise / 100));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await onSave({ amount: amount.trim(), reference: reference.trim() });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };
  return (
    <Dialog title="Mark payment received"
      subtitle={`For payments made outside Razorpay (bank transfer / UPI). Amount due: ${formatMoney(order.final_total_paise, order.currency)}.${order.payment_link_status === 'created' ? ' The unpaid Razorpay link will be cancelled.' : ''}`}
      onClose={onClose}>
      <form onSubmit={submit}>
        <label className="login-field">
          <span>Amount received ({order.currency}) *</span>
          <input type="number" min="0" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} />
        </label>
        <label className="login-field">
          <span>Payment reference *</span>
          <textarea rows={3} value={reference} onChange={(event) => setReference(event.target.value)} autoFocus
            placeholder="e.g. UPI / bank reference, account it came into, Backend Payments entry" />
        </label>
        {error && <div className="login-error">{error}</div>}
        <DialogActions onCancel={onClose} busy={busy} submitLabel="Mark as received" disabled={!amount.trim() || !reference.trim()} />
      </form>
    </Dialog>
  );
}

export default IntlOrderDetail;
